import config from 'js/app/config';

import type {
  SearchHitMetadataInputForSummary,
  SearchProductHit,
  SearchRequestOrigin,
} from 'bundles/search-common/providers/searchTypes';
import AI_SUMMARY_QUERY from 'bundles/search-common/queries/aiSummaryQuery.graphql';

// Types matching the actual GraphQL schema
export type SummarizeSearchResultsInput = {
  originalSearchRequest: SearchRequest;
  topSearchResults: SearchHitMetadataInputForSummary[];
};

export type SearchRequest = {
  entityType: string;
  query: string;
  limit?: number;
  facets?: string[];
  facetFilters?: string[];
  maxValuesPerFacet?: number;
  cursor?: string;
  sortBy?: string;
  disableRecommender?: boolean;
  enableAutoAppliedFilters?: boolean;
  explain?: boolean;
  requestOrigin?: SearchRequestOrigin;
};

export type SummarizeSearchResultsResponse = {
  summary: string;
  suggestions: string[];
};

export type SearchResultItem = {
  id: string;
  title: string;
  description?: string;
  productType: string;
};

export type UserContext = {
  userId?: string;
  preferences?: string[];
};

// Mock data for AI Summary
export const mockAISummaryResponse: SummarizeSearchResultsResponse = {
  summary: `Based on your search for "machine learning", I found several comprehensive courses and specializations that can help you build expertise in this field. 

The results include beginner-friendly courses like {machine-learning-for-everyone} from the University of London, which provides a solid foundation without requiring extensive programming knowledge. For more advanced learners, {stanford-machine-learning} by Andrew Ng remains one of the most popular and well-regarded options, covering algorithms, neural networks, and practical applications.

Several specializations are available that combine multiple courses, such as {machine-learning-specialization} and {deep-learning-specialization}, which provide structured learning paths from basics to advanced topics like computer vision and natural language processing.

Many courses offer hands-on projects using popular tools like Python, TensorFlow, and scikit-learn, allowing you to build a portfolio while learning. The courses range from 4-12 weeks in duration, with options for both self-paced and instructor-led formats.`,

  suggestions: [
    'What are the best beginner machine learning courses?',
    'How long does it take to learn machine learning?',
    'What programming languages do I need for machine learning?',
    'Are there any free machine learning courses available?',
  ],
};

export const mockAISummaryLoadingResponse: SummarizeSearchResultsResponse = {
  summary: '',
  suggestions: [],
};

export const mockAISummaryNotEligibleResponse: SummarizeSearchResultsResponse = {
  summary: '',
  suggestions: [],
};

// Mock search results that correspond to the slugs in the summary
export const mockSearchResults: Partial<SearchProductHit>[] = [
  {
    id: 'machine-learning-for-everyone',
    name: 'Machine Learning for Everyone',
    translatedName: 'Machine Learning for Everyone',
    productType: 'COURSE',
    parentCourseName: 'University of London',
    translatedParentCourseName: 'University of London',
  },
  {
    id: 'stanford-machine-learning',
    name: 'Machine Learning',
    translatedName: 'Machine Learning',
    productType: 'COURSE',
    parentCourseName: 'Stanford University',
    translatedParentCourseName: 'Stanford University',
  },
  {
    id: 'machine-learning-specialization',
    name: 'Machine Learning Specialization',
    translatedName: 'Machine Learning Specialization',
    productType: 'SPECIALIZATION',
    parentCourseName: 'DeepLearning.AI',
    translatedParentCourseName: 'DeepLearning.AI',
  },
  {
    id: 'deep-learning-specialization',
    name: 'Deep Learning Specialization',
    translatedName: 'Deep Learning Specialization',
    productType: 'SPECIALIZATION',
    parentCourseName: 'DeepLearning.AI',
    translatedParentCourseName: 'DeepLearning.AI',
  },
];
// Helper function to build full URL from base and path
const buildFullUrl = (path?: string | null): string | undefined => {
  if (!path) return undefined;
  // Remove trailing slash from base URL to avoid double slashes
  const baseUrl = config.url.base.replace(/\/$/, '');
  return `${baseUrl}${path}`;
};

// Helper function to convert SearchProductHit to SearchHitMetadataInputForSummary
export const convertSearchHitsToResultItems = (hits: SearchProductHit[]): SearchHitMetadataInputForSummary[] => {
  const resultItems = hits.map((hit) => {
    const fullUrl = buildFullUrl(hit.url || undefined);
    return {
      id: hit.id || '',
      name: hit.name,
      productType: hit.productType || undefined,
      avgProductRating: hit.avgProductRating,
      fullyTranslatedLanguages: hit.fullyTranslatedLanguages,
      isNewContent: hit.isNewContent,
      partners: hit.partners,
      productDifficultyLevel: hit.productDifficultyLevel,
      productDuration: hit.productDuration,
      skills: hit.skills,
      url: fullUrl,
    };
  });
  return resultItems;
};

// Mock variables for testing
export const mockAISummaryVariables = {
  originalSearchRequest: {
    entityType: 'PRODUCTS',
    query: 'machine learning',
    limit: 12,
    maxValuesPerFacet: 1000,
    cursor: '0',
    sortBy: 'BEST_MATCH',
    disableRecommender: false,
    enableAutoAppliedFilters: false,
    explain: true,
    requestOrigin: {
      pageType: 'SERP',
      segmentType: 'CONSUMER',
    },
  },
  topSearchResults: [
    {
      id: 'ml-course-1',
      name: 'Machine Learning for Everyone',
      productType: 'COURSE',
      avgProductRating: 4.5,
      fullyTranslatedLanguages: undefined,
      isNewContent: false,
      partners: ['University of London'],
      productDifficultyLevel: 'BEGINNER',
      productDuration: 'ONE_TO_THREE_MONTHS',
      skills: ['Machine Learning', 'Python'],
      url: buildFullUrl('/learn/machine-learning-for-everyone'),
    },
  ],
};

// Apollo MockedProvider mock
export const mockAISummaryQuery = {
  request: {
    query: AI_SUMMARY_QUERY,
    variables: mockAISummaryVariables,
  },
  result: {
    data: {
      SearchResult: {
        summarizeSearchResults: mockAISummaryResponse,
        __typename: 'SearchResultQueries',
      },
    },
  },
};

export const mockAISummaryQueryLoading = {
  request: {
    query: AI_SUMMARY_QUERY,
    variables: mockAISummaryVariables,
  },
  delay: 2000, // 2 second delay to simulate loading
  result: {
    data: {
      SearchResult: {
        summarizeSearchResults: mockAISummaryResponse,
        __typename: 'SearchResultQueries',
      },
    },
  },
};

export const mockAISummaryQueryError = {
  request: {
    query: AI_SUMMARY_QUERY,
    variables: mockAISummaryVariables,
  },
  error: new Error('Failed to generate AI summary'),
};
